import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Search, Upload, Film, Plus, Crown, Loader2 } from 'lucide-react';
import { fmtTime } from './EditorTimelineRuler';

/**
 * Media category panel (sidebar-driven, see EditorToolSidebar). Lists
 * every source file belonging to the current Parent Asset (the Parent
 * itself + every Child) so the Main Editor can build one Master timeline
 * out of multiple uploaded sources. Dragging an item onto a video track in
 * EditorMultiTrackTimeline — or clicking its quick-add button — places it
 * on the timeline; the source record itself is never duplicated or
 * modified. Text/Brand Kit/AI Tools/Publish moved out to their own
 * sidebar categories (Stage: Main Editor layout redesign) — this panel is
 * Media only now, reusing the exact same search/upload/source-list/
 * quick-add behavior as before.
 */
export function EditorMediaPanel({ sources, parentAssetId, onQuickAdd, uploadTo = '/app/create/content-studio', libraryTo = '/app/create/content-studio', startFromVideos = [], startFromLoading = false, onStartFromVideo, context = 'timeline' }) {
  const isClips = context === 'clips';
  const isDesign = context === 'design';
  const COPY = {
    timeline: { section: (n) => `Project Media (${n}) — drag onto a video or audio track`, addTitle: 'Add to timeline', emptyHint: 'Upload or pick from your Library above to start building the timeline.' },
    clips: { section: (n) => `Project Media (${n}) — pick one to work on in Clips`, addTitle: 'Use this video', emptyHint: 'Upload or pick from your Library above to start generating clips.' },
    design: { section: (n) => `Project Media (${n}) — add to your canvas`, addTitle: 'Add to canvas', emptyHint: 'Upload or pick from your Library above to start designing.' },
  }[context] || { section: (n) => `Project Media (${n})`, addTitle: 'Add', emptyHint: '' };
  const [query, setQuery] = useState('');

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return sources;
    return sources.filter((s) => (s.title || s.id).toLowerCase().includes(q));
  }, [sources, query]);

  const handleDragStart = (e, source) => {
    e.dataTransfer.setData(
      'application/x-consistency-media',
      JSON.stringify({ contentSourceId: source.id, durationSeconds: source.duration_seconds || 0 })
    );
    e.dataTransfer.effectAllowed = 'copy';
  };

  return (
    <div className="h-full flex flex-col" data-testid="editor-media-panel">
      {!parentAssetId && (startFromLoading || startFromVideos.length > 0) && (
        <div className="mb-3 p-2.5 rounded-lg border border-dashed border-white/15 bg-white/[0.02]" data-testid="media-start-from-existing">
          <p className="text-[11px] uppercase tracking-wide text-[#555E6E] mb-1.5">{isDesign ? 'Or start from existing media' : 'Or start from an existing video'}</p>
          {startFromLoading ? (
            <p className="text-xs text-[#8A93A6] flex items-center gap-1.5"><Loader2 className="h-3 w-3 animate-spin" />Loading your project videos…</p>
          ) : (
            <Select onValueChange={onStartFromVideo}>
              <SelectTrigger className="h-8 w-full text-xs bg-white/5 border-white/10" data-testid="media-start-from-select">
                <SelectValue placeholder={isDesign ? 'Pick media already in this project' : 'Pick a video already in this project'} />
              </SelectTrigger>
              <SelectContent>
                {startFromVideos.map((a) => (
                  <SelectItem key={a.id} value={a.id} data-testid={`media-start-from-option-${a.id}`}>{a.title || a.input_file_url?.split('/').pop() || 'Untitled asset'}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>
      )}
      <div className="relative mb-3">
        <Search className="h-3.5 w-3.5 text-[#8A93A6] absolute left-2.5 top-1/2 -translate-y-1/2" />
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search media"
          className="h-8 pl-8 bg-[#1C1F26] border-white/10 text-white text-sm"
          data-testid="media-search-input"
        />
      </div>

      <div className="grid grid-cols-2 gap-1.5 mb-3">
        <Link
          to={uploadTo}
          className="flex items-center justify-center gap-1.5 h-8 rounded-md border border-dashed border-white/20 text-xs text-[#8A93A6] hover:text-white hover:border-white/40 transition-colors"
          data-testid="media-upload-link"
        >
          <Upload className="h-3.5 w-3.5" />Upload
        </Link>
        <Link
          to={libraryTo}
          className="flex items-center justify-center gap-1.5 h-8 rounded-md border border-dashed border-white/20 text-xs text-[#8A93A6] hover:text-white hover:border-white/40 transition-colors"
          data-testid="media-library-link"
        >
          <Film className="h-3.5 w-3.5" />Library
        </Link>
      </div>

      <p className="text-[11px] uppercase tracking-wide text-[#555E6E] mb-2">{COPY.section(filtered.length)}</p>
      <div className="space-y-1.5 overflow-y-auto flex-1 pr-1" data-testid="media-source-list">
        {filtered.map((s) => (
          <div
            key={s.id}
            draggable={!isClips && !isDesign}
            onDragStart={(isClips || isDesign) ? undefined : (e) => handleDragStart(e, s)}
            className="group relative flex items-center gap-2 p-2 rounded-lg border border-white/10 bg-white/5 hover:border-[#3A7BFF]/60 cursor-grab active:cursor-grabbing transition-colors"
            data-testid={`media-source-item-${s.id}`}
          >
            <div className="h-9 w-14 rounded bg-black/50 flex items-center justify-center shrink-0 overflow-hidden">
              <Film className="h-4 w-4 text-[#555E6E]" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-xs text-white truncate" data-testid={`media-source-title-${s.id}`}>{s.title || s.id}</p>
              <div className="flex items-center gap-1.5">
                {s.id === parentAssetId && (
                  <span className="inline-flex items-center gap-0.5 text-[9px] text-amber-400" data-testid={`media-source-parent-badge-${s.id}`}>
                    <Crown className="h-2.5 w-2.5" />Parent
                  </span>
                )}
                <span className="text-[10px] text-[#8A93A6]">{s.duration_seconds ? fmtTime(s.duration_seconds) : '—'}</span>
              </div>
            </div>
            <button
              onClick={() => onQuickAdd(s)}
              className="shrink-0 h-6 w-6 rounded-full bg-[#3A7BFF]/20 text-[#3A7BFF] hover:bg-[#3A7BFF] hover:text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
              title={COPY.addTitle}
              data-testid={`media-source-add-${s.id}`}
            >
              <Plus className="h-3.5 w-3.5" />
            </button>
          </div>
        ))}
        {filtered.length === 0 && sources.length > 0 && (
          <p className="text-xs text-[#8A93A6] text-center py-6" data-testid="media-source-list-empty">No sources match your search.</p>
        )}
        {sources.length === 0 && (
          <div className="text-center py-8" data-testid="media-source-list-no-assets">
            <Film className="h-6 w-6 text-[#3A3F4B] mx-auto mb-2" />
            <p className="text-xs text-[#8A93A6]">No media in this project yet.</p>
            <p className="text-[11px] text-[#555E6E] mt-0.5">{COPY.emptyHint}</p>
          </div>
        )}
      </div>

      {!isClips && !isDesign && (
        <>
          <p className="text-[11px] uppercase tracking-wide text-[#555E6E] mt-3 mb-1">Processed Media (0)</p>
          <p className="text-[11px] text-[#555E6E] pb-1" data-testid="media-processed-empty">Rendered clips will appear here once available.</p>
        </>
      )}
    </div>
  );
}
